/**
 * useItemLocations (SR3.2, plan §WR3) — per-location NetSuite availability
 * read-through for the 各地點可供量 Item Locations section (DD!179 Must).
 *
 * A Tier-1 read-through: the ERP web never owns on-hand (two-tier boundary,
 * Fixed decision 3), so this hook only FETCHES the mocked SR0.1 endpoint
 * `/netsuite/availability/:productId/locations` and surfaces its state — it
 * never computes or stores a quantity. LOADING + hard-ERROR are exposed UP
 * FRONT so the consuming section renders skeleton / retry off this hook without
 * re-deriving state; the empty case (an unmapped product → `netSuiteItemId:
 * null`) is a DEFINED response, not an error.
 *
 * `ready`-gated so `isLoading` stays true until the first fetch resolves for a
 * real productId. Transactional freshness (short staleTime) — every read is a
 * fresh read-through moment; `syncedAt` rides the response and is UI-visible on
 * the section (SourceStampTag + stale badge).
 *
 * Called directly on `apiClient` (there is no `productApi` service method for
 * the NetSuite read-through yet — mirrors `useProductSalesStats`; see followUp).
 */

import { clearCache, useRequest } from 'ahooks';
import apiClient from '@/services/api/axios.config';
import type { NetSuiteItemLocationsResponse } from '@/types/api/purchaseOrder';

/** Static, /api/-prefixed cacheKey (house rule). */
export const itemLocationsCacheKey = (productId: string) =>
  `/api/netsuite/availability/${productId}/locations`;

export function useItemLocations(productId?: string) {
  const { data, error, loading, run } = useRequest(
    async () => {
      const response = await apiClient.get<NetSuiteItemLocationsResponse>(
        `/netsuite/availability/${productId}/locations`
      );
      return response.data;
    },
    {
      ready: !!productId,
      refreshDeps: [productId],
      cacheKey: productId ? itemLocationsCacheKey(productId) : undefined,
      // Transactional read-through — a stale snapshot is surfaced, never hidden.
      staleTime: 30000,
    }
  );

  return {
    /** Full read-through response (rows + server-computed totals + syncedAt). */
    data,
    /** Per-location rows; server totals sum to the header `availableQty`. */
    rows: data?.rows ?? [],
    /** Server-computed Σ across `rows` (bold totals row). */
    totals: data?.totals,
    /** `null` when the product is not linked to a NetSuite item → empty state. */
    netSuiteItemId: data?.netSuiteItemId ?? null,
    /** Read-through moment — drives the SourceStampTag + freshness badge. */
    syncedAt: data?.syncedAt,
    error,
    isLoading: loading,
    /** Invalidate + re-run (never bare ahooks refresh in components). */
    refresh: () => {
      if (productId) clearCache(itemLocationsCacheKey(productId));
      return run();
    },
  };
}

export default useItemLocations;
